import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Clock } from 'lucide-react';
import { formatDateOnlyForStorage } from '../Helpers/DateUtils';

interface SnoozeModalProps {
    show: boolean;
    onHide: () => void;
    itemName: string;
    onSnooze: (snoozeDate: string) => Promise<void>;
}

export const SnoozeModal: React.FC<SnoozeModalProps> = ({
    show,
    onHide,
    itemName,
    onSnooze
}) => {
    const [snoozeDate, setSnoozeDate] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Calculate tomorrow's date for min attribute
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = formatDateOnlyForStorage(tomorrow);

    // Reset form when modal opens
    useEffect(() => {
        if (show) {
            setSnoozeDate('');
            setError(null);
        }
    }, [show]);

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        if (!snoozeDate) {
            setError('Välj ett datum');
            return;
        }

        // Validate date is in the future
        const today = formatDateOnlyForStorage(new Date());
        if (snoozeDate <= today) {
            setError('Datum måste vara i framtiden');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            await onSnooze(snoozeDate);
            onHide();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Kunde inte snooze';
            setError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            setSnoozeDate('');
            setError(null);
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Snooze</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                            {error}
                        </Alert>
                    )}
                    <p className="text-muted mb-3">
                        {itemName} kommer att döljas tills det valda datumet.
                    </p>
                    <Form.Group>
                        <Form.Label>Snooze till *</Form.Label>
                        <Form.Control
                            type="date"
                            value={snoozeDate}
                            onChange={(e) => setSnoozeDate(e.target.value)}
                            min={minDate}
                            required
                            autoFocus
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting || !snoozeDate}>
                        <Clock size={18} className="me-1" />
                        {submitting ? 'Sparar...' : 'Snooze'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
